import useApiHelpers from "@/composables/api/helpers";
import {
  isValueString,
  lowerCaseItem,
} from "@/composables/helpers/useStringHelpers.js";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import { useGlobalStore } from "@/stores/globalStore";

const { getRequest, postRequest } = useApiHelpers();

export default function usePaymentEndpoints() {
  const getPayments = async () => {
    const response = await getRequest(`/payment-methods`);
    return response;
  };

  const getWorldPayToken = async () => {
    const response = await getRequest(`/prepaid`);
    return response;
  };

  const getCheckoutCCSettings = async () => {
    const response = await getRequest("/settings/prepaid/checkout");
    return response;
  };

  const getCircleAchSettings = async () => {
    const response = await getRequest("/settings/prepaid/circleach");
    return response;
  };

  const getFortressAchSettings = async () => {
    const response = await getRequest("/settings/prepaid/fortress");
    return response;
  };

  const getPlaidOpenBankSettings = async () => {
    const response = await getRequest("/settings/prepaid/plopenbank");
    return response;
  };

  const paymentCallback = async (to, next) => {
    const orderStore = useOrderFormStore();

    const { payment } = to.params;
    const { status } = to.params;
    const { orderId } = to.query;

    const paymentTypeCode = orderStore.getPaymentObject?.pspTypeCode;

    // TODO throw an error message if the payment from redirect url does not match with the payment saved in session
    // if(payment != buyStore.getPaymentObject?.value) throw error
    switch (paymentTypeCode?.toUpperCase()) {
      case "IDEAL":
        await getRequest(
          `/payment/${payment}/callback/${status}?${new URLSearchParams({
            orderId: to.query.orderId,
          }).toString()}`,
          (response) => {
            next(`/status/${response.data.orderId}`);
          }
        );
        break;
      case "DLOCALZAIO":
      case "DLOCALPIX":
      case "DLOCALSPEI":
      case "DLOCALARIO":
      case "DLOCALCOPSE":
      case "DLOCALCLKH":
      case "DLOCALTHQR":
        await getRequest(
          `/payment/${payment}/callback/${status}?${new URLSearchParams({
            orderId: to.query.orderId,
            orderKey: to.query.orderKey,
          }).toString()}`,
          () => {
            next(`/status/${to.query.orderKey}`);
          }
        );
        break;
      case "POLI":
        await getRequest(
          `/payment/${payment}/callback/${status}?${new URLSearchParams({
            token: to.query.token,
          }).toString()}`,
          (response) => {
            next(`/status/${response.data.orderId}`);
          }
        );
        break;
      case "WORLDPAYGOOGLE":
        await getRequest(
          `/payment/${payment}/callback/${status}?${new URLSearchParams({
            orderId: to.query.orderId,
          }).toString()}`,
          () => {
            next(`/status/${orderId}`);
          }
        );

        break;
      case "CHECKOUTCREDIT":
        const ckoSessionId = to.query["cko-session-id"];
        checkoutCallbackTo1Cart(payment, status, orderId, ckoSessionId);
        localStorage.setItem("isPaymentCompleted", false);
        if (isValueString(status) && lowerCaseItem(status) === "success") {
          // Redirect to Order Status Page
          localStorage.setItem("isPaymentCompleted", true);
          next(`/status/${orderId}`);
        }
        // checkoutCallbackTo1Cart will redirect the user to the error page if status is not 'success'
        break;
      default:
    }
  };

  const checkoutCallbackTo1Cart = async (
    payment,
    status,
    orderId,
    ckoSessionId
  ) => {
    await getRequest(
      `/payment/${payment}/callback/${status}?orderId=${orderId}&cko-session-id=${ckoSessionId}`
    );
  };

  const getApplePayPageProps = async () => {
    const res = await getRequest("/prepaid/applepay");
    return res.data.data.options;
  };

  const getGooglePayPageProps = async () => {
    const res = await getRequest("/prepaid/gpay");
    return res.data.data.options;
  };

  const logApplePayErrorsApplePayErrors = (error) => {
    console.log(error);
    postRequest("/logging/log", {
      data: error,
    });
  };

  const validateApplePaySessionEndpoint = (
    validateSessionURL,
    subdomain,
    appleUrl,
    appleSuccessCallback,
    appleFailureCallback
  ) => {
    postRequest(
      validateSessionURL,
      { validateURL: appleUrl, subdomain },
      // success callback
      (response) => {
        if (response.data.error) {
          console.error("failure validating session, ", response.data.error);
          appleFailureCallback(response.data.message);
        } else {
          appleSuccessCallback(response.data);
        }
      },
      // failure callback
      (error) => {
        console.error("failure validating session, ", error);
        const { response } = error;
        const errorMsg = response.data.message;

        appleFailureCallback(errorMsg);
      },
      { headers: { "Access-Control-Allow-Origin": "*" } }
    );
  };

  const postCircleAchPaymentCallback = (
    callbackUrl,
    payload,
    successCallback,
    pendingCallback
  ) => {
    postRequest(callbackUrl, payload, (res) => {
      const response = res.data?.status;
      const orderId = res.data?.orderId;
      if (response === "success" && orderId) {
        successCallback(orderId);
        return;
      }
      pendingCallback();
    });
  };

  const postPlaidDataCallback = (
    callbackUrl,
    payload,
    successCallback,
    failureCallback
  ) => {
    postRequest(
      callbackUrl,
      payload,
      (res) => {
        const response = res.data?.status;
        const orderId = res.data?.orderId;
        if (response === "success" && orderId) {
          successCallback(orderId);
        }
      },
      (failureRes) => {
        failureCallback(failureRes?.data);
      }
    );
  };

  return {
    getPayments,
    getWorldPayToken,
    getCheckoutCCSettings,
    getCircleAchSettings,
    getFortressAchSettings,
    paymentCallback,
    getApplePayPageProps,
    getGooglePayPageProps,
    validateApplePaySessionEndpoint,
    postCircleAchPaymentCallback,
    postPlaidDataCallback,
    getPlaidOpenBankSettings,
  };
}
